import type { AxiosError, AxiosResponse } from "axios";
import type {
  AddCustomEmailConfigurationInput,
  BillingMailbox,
  BillingMailboxSettings,
  CallBackOAuthInput,
  UpdateCustomEmailConfigurationInput,
  UpdateEmailConfigurationInput,
  VerifyCustomEmailConfigurationInput,
} from "shared";
import { BILLING_EMAIL_TYPE } from "shared/features/mailbox";
import { axiosClient } from "utilz";
import {
  apiErrorHandler,
  generateUrlString,
  removeEmptyValueFromObject,
} from "utilz/helpers";

/**
 * Billing settings needs both the primary and the dedicated billing mailbox at
 * once, and `fetchAllEmails` deliberately excludes billing mailboxes — hence a
 * dedicated route rather than a filter over the list.
 */
/**
 * Every billing-tagged mailbox, active and disabled. `.../billing-mailbox`
 * returns only the active one, so this is the only way to see the backups a
 * switch left behind. Exact-match filter — an unrecognised value returns [].
 */
export const fetchBillingMailboxes = async (): Promise<
  BillingMailbox[] | null
> => {
  try {
    const url = "/v1/servicedesk/channels/emails";
    const response = await axiosClient.get(url, {
      params: { email_type: BILLING_EMAIL_TYPE },
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getBillingMailboxSettings =
  async (): Promise<BillingMailboxSettings | null> => {
    try {
      const url = "/v1/servicedesk/channels/email/billing-mailbox";
      const response = await axiosClient.get(`${url}`);
      const resData = response.data;
      if (resData.success && resData.statusCode === 200) {
        return resData.data;
      }
      throw response;
    } catch (error) {
      return apiErrorHandler(error as AxiosError | AxiosResponse);
    }
  };

export const fetchAllEmails = async () => {
  try {
    const url = "/v1/servicedesk/channels/emails";
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const callBackOAuth = async ({
  mappingId,
  provider,
  ...data
}: CallBackOAuthInput) => {
  try {
    const url = await generateUrlString({
      baseUrl: `/v1/servicedesk/channels/email/callback/email-engine/${mappingId}/${provider}`,
      data: removeEmptyValueFromObject(data),
    });

    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const emailOauthSignIn = async (
  provider: "gmail" | "outlook",
  redirect?: "control_center" | "onboarding",
  sharedMailboxEmail?: string,
) => {
  const redirectUrl = redirect || "control_center";
  try {
    const url = `/v1/servicedesk/channels/email/oauth/signin/${provider}`;
    const response = await axiosClient.get(`${url}`, {
      params: {
        redirect,
        frontendUrl: window.location.origin,
        ...(sharedMailboxEmail
          ? {
              account_type: "delegation",
              shared_mailbox_emails: sharedMailboxEmail,
            }
          : {}),
      },
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateEmailConfiguration = async ({
  mappingId,
  ...data
}: UpdateEmailConfigurationInput) => {
  try {
    const url = `/v1/servicedesk/channels/email/${mappingId}`;
    const response = await axiosClient.patch(
      `${url}`,
      removeEmptyValueFromObject(data),
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const fetchAllEmailAssignees = async ({
  mappinId,
}: {
  mappinId: string;
}) => {
  try {
    const url = `/v1/servicedesk/channels/email/assignees/${mappinId}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateEmailAccount = async ({
  mappingId,
}: {
  mappingId: string;
}) => {
  try {
    const url = `/v1/servicedesk/channels/email/oauth/change-account/${mappingId}`;
    const response = await axiosClient.patch(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const deleteEmailServerConfiguration = async ({
  mappingId,
}: {
  mappingId: string;
}) => {
  try {
    const url = `/v1/servicedesk/channels/email/${mappingId}`;
    const response = await axiosClient.delete(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getTestEmailConnection = async (mappingId: string) => {
  try {
    const url = `/v1/servicedesk/channels/email/delivery-test/account/${mappingId}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const addCustomEmailConfiguration = async ({
  ...data
}: AddCustomEmailConfigurationInput) => {
  try {
    const url = "/v1/servicedesk/channels/email/custom/add";
    const response = await axiosClient.post(
      `${url}`,
      removeEmptyValueFromObject(data),
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getCustomEmailConfiguration = async ({
  mappingId,
}: {
  mappingId: string;
}) => {
  try {
    const url = `/v1/servicedesk/channels/email/custom/${mappingId}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateCustomEmailConfiguration = async ({
  ...data
}: UpdateCustomEmailConfigurationInput) => {
  try {
    const url = "/v1/servicedesk/channels/email/custom/update";
    const response = await axiosClient.post(
      `${url}`,
      removeEmptyValueFromObject(data),
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const verifyCustomEmailConfiguration = async ({
  ...data
}: VerifyCustomEmailConfigurationInput) => {
  try {
    const url = "/v1/servicedesk/channels/email/custom/verify";
    const response = await axiosClient.post(
      `${url}`,
      removeEmptyValueFromObject(data),
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};
